<script lang="ts" setup>
import {
  tpgIconNotifications,
  tpgIconNotificationsOff,
} from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type {
  GrammarMenuItemProps,
} from '@nora-monorepo/grammar-ui';
import {
  GrammarMenuItem,
  GrammarQuaternaryMenuButton,
} from '@nora-monorepo/grammar-ui';
import {
  checkIsMuted,
  CustomDurationModal,
} from '@nora-monorepo/ui-shared';

import { useChatUserStore } from '#nora-chat-ui/stores/user-store';

const { t, d } = useI18n();
const userStore = useChatUserStore();

const { setGlobalMute } = userStore;

const showCustomDurationModal = ref(false);
const { user, isMuted } = storeToRefs(userStore);

function getMuteMenuItems(): GrammarMenuItemProps[] {
  const items = [
    {
      label: `30 ${t('common.time.minutes')}`,
      onSelect: () => {
        const date = new Date();
        date.setMinutes(date.getMinutes() + 30);
        setGlobalMute({ chatMutedUntil: date, chatMutedIndefinite: false });
      },
    },
    {
      label: `1 ${t('common.time.hour')}`,
      onSelect: () => {
        const date = new Date();
        date.setMinutes(date.getMinutes() + 60);
        setGlobalMute({ chatMutedUntil: date, chatMutedIndefinite: false });
      },
    },
    {
      label: `8 ${t('common.time.hours')}`,
      onSelect: () => {
        const date = new Date();
        date.setHours(date.getHours() + 8);
        setGlobalMute({ chatMutedUntil: date, chatMutedIndefinite: false });
      },
    },
    {
      label: t('notifications.muteIndefinite'),
      onSelect: () => {
        setGlobalMute({ chatMutedIndefinite: true });
      },
    },
    {
      label: t('common.custom'),
      onSelect: () => {
        showCustomDurationModal.value = !showCustomDurationModal.value;
      },
    },
  ];

  if (isMuted.value) {
    return [
      {
        label: t('notifications.resume'),
        onSelect: () => {
          setGlobalMute({
            chatMutedIndefinite: false,
            chatMutedUntil: undefined,
          });
        },
      },
      ...items,
    ];
  }
  else {
    return items;
  }
}

const menuItems = computed<GrammarMenuItemProps[]>(() => getMuteMenuItems());

const mutedLabel = computed(() => {
  if (!user.value) {
    return '';
  }

  if (
    !checkIsMuted(user.value.chatMutedIndefinite, user.value.chatMutedUntil)
  ) {
    return '';
  }

  if (user.value.chatMutedIndefinite) {
    return t('notifications.muteIndefinite');
  }

  if (!user.value.chatMutedUntil) {
    return 'Active';
  }

  const remainingTime = user.value.chatMutedUntil.getTime() - Date.now();

  if (remainingTime > 60 * 60 * 24 * 1000) {
    return t('notifications.mutedUntil', [
      d(new Date(user.value.chatMutedUntil), 'short'),
    ]);
  }

  return t('notifications.mutedUntil', [
    d(new Date(user.value.chatMutedUntil), 'time'),
  ]);
});

function onConfirm(date: Date) {
  showCustomDurationModal.value = false;

  setGlobalMute({ chatMutedUntil: date, chatMutedIndefinite: false });
}
</script>

<template>
  <GrammarQuaternaryMenuButton
    :icon-template="isMuted ? tpgIconNotificationsOff : tpgIconNotifications"
    :label="mutedLabel"
  >
    <GrammarMenuItem
      v-for="(item, key) in menuItems"
      :key="key"
      v-bind="item"
    />
  </GrammarQuaternaryMenuButton>

  <CustomDurationModal
    :header-title="t('notifications.customNotificationsHeader')"
    :modal-active="showCustomDurationModal"
    :on-cancel="
      () => {
        showCustomDurationModal = false;
      }
    "
    :initial-date="user?.chatMutedUntil as Date"
    :on-confirm="onConfirm"
  />
</template>
